// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { Dictionary } from "@insite/client-framework/Common/Types";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { addToChainAfter, addToChainBefore } from "@insite/client-framework/HandlerCreator";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    chain,
    DispatchCompleteLoadProducts,
    GetUnfilteredProducts,
    ParseQueryParameter,
    PopulateApiParameter,
} from "@insite/client-framework/Store/Pages/ProductList/Handlers/DisplayProducts";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import { AttributeTypeFacetModel } from "@insite/client-framework/Types/ApiModels";
import { chain as chainWithoutRealtime } from "@pacific/App/Handlers/ProductList/DisplayProductsWithoutRealtime";
import { getGroupByAttributes } from "@pacific/App/Services/CustomProductService";
import { CustomAppState } from "@pacific/App/Store/Reducers";
import { CustomCatalogPageState, GroupByAttribute } from "@pacific/XDL/types";
import { colonDelimeter, levelDelimeter } from "@pacific/XDL/Utilities/helpers";
import get from "lodash/get";
import set from "lodash/set";
import qs from "qs";

export const XngPopulateApiParameter: any = (props: any) => {
    props.apiParameter = {
        ...props.apiParameter,
    };

    if (props.apiParameter.categoryId) {
        props.apiParameter = {
            ...props.apiParameter,
            // this controls the showing products in category pages
            includeProductsInSubCategories: !!props.parameter?.includeProductsInSubCategories,
            // to show facets filters in category pages
            restrictAttributesToCategory: !!props.parameter?.restrictAttributesToCategory,
        };
    }
};

/** We should remove includeProductsInSubCategories from ApiParameter because this option need CategoryId
 * and GetUnfilteredProducts remove CategoryId from ApiParameter
 */
export const XngGetUnfilteredProducts: any = (props: any) => {
    const apiParameter = { ...props.apiParameter };
    if (!props.result.productFilters.pageCategoryId) {
        if (props.apiParameter.includeProductsInSubCategories) {
            delete apiParameter.includeProductsInSubCategories;
        }
        if (props.apiParameter.restrictAttributesToCategory) {
            delete apiParameter.restrictAttributesToCategory;
        }
        props.apiParameter = {
            ...apiParameter,
        };
    }

    delete apiParameter.onlyAvailableInCurrentBranch;
};

export const XngGetGroupByAttributesFromApi: any = async (props: any) => {
    const state = props.getState();
    const location = getLocation(state);
    const searchRequestType = parseQueryString<{ searchRequestType: string }>(location.search).searchRequestType;
    const searchImageKey = parseQueryString<{ searchImageKey: string }>(location.search).searchImageKey;
    props.result.filteredApiParameter = { ...props.apiParameter, searchRequestType, searchImageKey };
    props.apiResult = await getGroupByAttributes(props.apiParameter);
};

export const XngGroupLevelsForTableView: typeof DispatchCompleteLoadProducts = (props: any) => {
    const { catalogPage } = props.result;
    if (!catalogPage) {
        return;
    }

    const groupByAttributes = props.apiResult.groupByAttributes as GroupByAttribute[];
    const { columnsAttributeTypes } = catalogPage as CustomCatalogPageState;

    const state = props.getState() as CustomAppState;
    const attributeTypeFacets = getProductListDataViewProperty(state, "attributeTypeFacets");
    const hasGrouping = groupByAttributes && groupByAttributes.length;

    if (!attributeTypeFacets || !hasGrouping) {
        return;
    }

    const groupByAttributesHashMap = groupByAttributes.reduce<Dictionary<GroupByAttribute>>((acc, curr) => {
        acc[curr.groupKey] = curr;
        return acc;
    }, {});

    const groupedLevels: any = {};
    const levelImagesMap: any = {};
    Object.keys(groupByAttributesHashMap)
        .sort()
        .forEach((item: string) => {
            let sp = item.split(levelDelimeter).map(key => key.replace(colonDelimeter, ": "));
            sp = sp.length > 2 ? sp.slice(0, sp.length - 1) : sp;
            const { imagePath, imageAltText } = groupByAttributesHashMap[item];
            levelImagesMap[sp[0]] = { imagePath, imageAltText };
            sp.reduce<string>((acc, curr, index) => {
                acc += `['${curr}']`;
                set(groupedLevels, acc, {
                    ...(get(groupedLevels, acc) || {}),
                    valueId: groupByAttributesHashMap[item].attributeValueIds[index],
                    productsById: index ? {} : undefined,
                });
                return acc;
            }, "");
        });
    const attributeTypeFacetsHashMap = attributeTypeFacets.reduce<Dictionary<AttributeTypeFacetModel>>((acc, curr) => {
        acc[curr.attributeTypeId] = curr;
        return acc;
    }, {});

    const columns = columnsAttributeTypes.map(columnId => attributeTypeFacetsHashMap[columnId]).filter(Boolean);
    const sortedColumns = columns.sort((c1, c2) => c1.sortOrder - c2.sortOrder);
    const levels = groupByAttributes[0].groupKey.split("þ").map((keyValue: string) => {
        const attributeName = keyValue.split("ý")[0];
        return attributeTypeFacets.find(d => d.nameDisplay === attributeName || d.name === attributeName);
    });

    props.dispatch({
        type: "xProductList/FilterDrawer/CompleteTableGrouping",
        data: {
            hasGrouping: true,
            levelsMap: groupedLevels,
            productListTableColumns: sortedColumns,
            levelImagesMap,
            levels,
            categoryId: catalogPage.categoryId,
        },
    } as any);
};

export const XngParseQueryParameter: typeof ParseQueryParameter = props => {
    const { queryString } = props.parameter;
    const parsedQuery = qs.parse(queryString.startsWith("?") ? queryString.substr(1) : queryString);
    const { onlyAvailableInCurrentBranch } = parsedQuery;

    if ((props.parameter as any).pageSize) {
        props.result.productFilters.pageSize = (props.parameter as any).pageSize;
    }

    if ((props.parameter as any).page) {
        props.result.productFilters.page = (props.parameter as any).page;
    }

    if (onlyAvailableInCurrentBranch !== undefined) {
        (props.result.productFilters as any).onlyAvailableInCurrentBranch = true;
    }
};

addToChainAfter(chain, PopulateApiParameter, XngPopulateApiParameter);
addToChainAfter(chain, ParseQueryParameter, XngParseQueryParameter);
addToChainBefore(chain, GetUnfilteredProducts, XngGetUnfilteredProducts);
addToChainAfter(chain, GetUnfilteredProducts, XngGetGroupByAttributesFromApi);
addToChainBefore(chain, DispatchCompleteLoadProducts, XngGroupLevelsForTableView);

addToChainAfter(chainWithoutRealtime, PopulateApiParameter, XngPopulateApiParameter);
addToChainAfter(chainWithoutRealtime, ParseQueryParameter, XngParseQueryParameter);
addToChainBefore(chainWithoutRealtime, GetUnfilteredProducts, XngGetUnfilteredProducts);
addToChainAfter(chainWithoutRealtime, GetUnfilteredProducts, XngGetGroupByAttributesFromApi);
addToChainBefore(chainWithoutRealtime, DispatchCompleteLoadProducts, XngGroupLevelsForTableView);
